
(() => {
  const escapeSelector = (value) => {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  class DekorkPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-predictive-results]');
      this.delay = Number(this.dataset.delay || 250);
      this.limit = Math.max(1, Math.min(10, Number(this.dataset.limit || 6)));
      this.sectionId = this.dataset.sectionId || 'dekork-predictive-search';
      this.enabled = this.dataset.enabled !== 'false';
      this.abortController = null;
      this.timer = null;
      this.activeIndex = -1;

      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.innerHTML.trim()) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form?.addEventListener('submit', () => {
        this.abortController?.abort();

        let typeInput = this.form.querySelector('input[name="type"]');
        if (!typeInput) {
          typeInput = document.createElement('input');
          typeInput.type = 'hidden';
          typeInput.name = 'type';
          this.form.appendChild(typeInput);
        }
        typeInput.value = 'product';

      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          this.close();
          this.input.blur();
        }
      });
    }

    schedule() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();

      if (!term) {
        this.abortController?.abort();
        this.abortController = null;
        this.classList.remove('is-loading');
        this.input.removeAttribute('aria-busy');
        this.results.innerHTML = '';
        this.close();
        return;
      }

      if (!this.enabled) return;

      /* Clear stale suggestions as soon as the query changes. The loading
         surface then owns the result area until this exact request finishes. */
      this.abortController?.abort();
      this.abortController = null;
      this.results.replaceChildren();
      this.activeIndex = -1;
      this.classList.add('is-loading');
      this.results.hidden = false;
      this.input.setAttribute('aria-busy', 'true');
      this.open();

      this.timer = window.setTimeout(() => this.fetchResults(term), this.delay);
    }

    async fetchResults(term) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.classList.add('is-loading');
      this.results.hidden = false;
      this.input.setAttribute('aria-busy', 'true');

      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}search/suggest`, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'show');
      url.searchParams.set('resources[options][fields]', 'title,variants.title');
      url.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector(`#shopify-section-${escapeSelector(this.sectionId)}`);
        const content = section?.querySelector('[data-dk-predictive-content]');

        if (this.input.value.trim() !== term) return;

        if (content) {
          const normalize = (value) => String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();

          const normalizedTerm = normalize(term);
          const items = Array.from(content.querySelectorAll('.dk-predictive__item'));
          let kept = 0;

          items.forEach((item) => {
            const searchableText = normalize(item.dataset.dkSearchIndex);
            const matches = searchableText.includes(normalizedTerm);

            if (!matches || kept >= this.limit) {
              item.remove();
              return;
            }

            kept += 1;
          });

          const metaCount = content.querySelector('.dk-predictive__meta span:last-child');
          if (metaCount) metaCount.textContent = `${kept} result${kept === 1 ? '' : 's'}`;

          if (kept === 0) {
            content.innerHTML = `
              <div class="dk-predictive__empty">
                <span class="dk-predictive__empty-mark" aria-hidden="true">⌕</span>
                <strong>No products found</strong>
                <p>Try another product name or variant.</p>
                <a href="${root}search?q=${encodeURIComponent(term)}&type=product">Search for “${term.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))}”</a>
              </div>`;
          }
        }

        this.results.innerHTML = content ? content.outerHTML : '';
        this.activeIndex = -1;
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.results.innerHTML = `
            <div class="dk-predictive__empty">
              <strong>Search is temporarily unavailable</strong>
              <p>Press Enter to open the search page.</p>
            </div>`;
          this.open();
        }
      } finally {
        /* An aborted older request must not dismiss a newer query's loader. */
        if (this.abortController === controller) {
          this.classList.remove('is-loading');
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    get options() {
      return Array.from(this.results.querySelectorAll('.dk-predictive__link'));
    }

    onKeydown(event) {
      const options = this.options;

      if (event.key === 'Escape') {
        const drawer = this.closest('[data-search-modal]');
        if (drawer) closeDrawer(drawer);
        else this.close();
        return;
      }

      if (!options.length || this.results.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % options.length;
        this.updateActive(options);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
        this.updateActive(options);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex].click();
      }
    }

    updateActive(options) {
      options.forEach((option, index) => {
        option.classList.toggle('is-keyboard-active', index === this.activeIndex);
        option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');
      });
      options[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
    }

    open() {
      if (!this.results.innerHTML.trim() && !this.classList.contains('is-loading')) return;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', DekorkPredictiveSearch);
  }

  let dkSearchScrollY = 0;

  function lockBodyScroll() {
    dkSearchScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('dk-search-is-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${dkSearchScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockBodyScroll() {
    document.body.classList.remove('dk-search-is-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, dkSearchScrollY);
  }

  function getDrawer(trigger) {
    const section = trigger.closest('.shopify-section, .section-header');
    return section?.querySelector('[data-search-modal]') || document.querySelector('[data-search-modal]');
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    window.requestAnimationFrame(() => {
      const input = drawer.querySelector('input[type="search"]');
      input?.focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    const predictive = drawer.querySelector('predictive-search');
    predictive?.close?.();
  }

  function bindSearchDrawers(scope = document) {
    scope.querySelectorAll('[data-search-open]').forEach((button) => {
      if (button.dataset.dkSearchBound === 'true') return;
      button.dataset.dkSearchBound = 'true';
      button.addEventListener('click', () => openDrawer(getDrawer(button)));
    });

    scope.querySelectorAll('[data-search-modal]').forEach((drawer) => {
      if (drawer.dataset.dkSearchBound === 'true') return;
      drawer.dataset.dkSearchBound = 'true';

      drawer.querySelectorAll('[data-search-close]').forEach((button) => {
        button.addEventListener('click', () => closeDrawer(drawer));
      });
    });
  }

  bindSearchDrawers();

  document.addEventListener('shopify:section:load', (event) => {
    bindSearchDrawers(event.target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-search-modal][aria-hidden="false"]').forEach(closeDrawer);
  });
})();
